/**
 * Come si scrive un numero in questo gestionale. Una sola porta, per tutta l'app.
 *
 * Perché esiste. `Intl.NumberFormat("it-IT")` da solo NON mette il punto delle
 * migliaia sotto le cinque cifre: l'italiano ha `minimumGroupingDigits = 2` nel
 * CLDR, quindi `4818,02` resta così mentre `15.790,92` viene raggruppato. Nella
 * stessa tabella si leggono due grafie diverse dello stesso tipo di numero — e
 * quel numero è il prezzo che il venditore dice a un cliente seduto davanti.
 * `useGrouping: "always"` toglie l'eccezione e i numeri tornano tutti uguali.
 *
 * L'allineamento delle colonne è l'altra metà: le cifre tabellari
 * (`font-variant-numeric: tabular-nums`) sono già sui `<table>` in `index.css`;
 * dove i numeri stanno in una griglia di `<div>` si aggiunge la classe Tailwind
 * `tabular-nums` a mano.
 */

const VUOTO = "—";

const cacheEuro = new Map();
function formattatoreEuro(decimali) {
  if (!cacheEuro.has(decimali)) {
    cacheEuro.set(
      decimali,
      new Intl.NumberFormat("it-IT", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: decimali,
        maximumFractionDigits: decimali,
        useGrouping: "always",
      }),
    );
  }
  return cacheEuro.get(decimali);
}

const cachePercentuale = new Map();
function formattatorePercentuale(decimali) {
  if (!cachePercentuale.has(decimali)) {
    cachePercentuale.set(
      decimali,
      new Intl.NumberFormat("it-IT", {
        style: "percent",
        minimumFractionDigits: decimali,
        maximumFractionDigits: decimali,
      }),
    );
  }
  return cachePercentuale.get(decimali);
}

/** Un dato che non c'è resta vuoto: non diventa uno zero. */
function mancante(valore) {
  if (valore === null || valore === undefined || valore === "") return true;
  return !Number.isFinite(Number(valore));
}

/**
 * Un importo in euro. `eur(4818.02)` → `"4.818,02 €"`.
 * `decimali: 0` per i riquadri di sintesi, dove i centesimi sono rumore.
 */
export function eur(valore, { decimali = 2, vuoto = VUOTO } = {}) {
  if (mancante(valore)) return vuoto;
  return formattatoreEuro(decimali).format(Number(valore));
}

/**
 * Una percentuale, da frazione. `percentuale(0.2539)` → `"25,4%"`.
 */
export function percentuale(frazione, { decimali = 1, vuoto = VUOTO } = {}) {
  if (mancante(frazione)) return vuoto;
  return formattatorePercentuale(decimali).format(Number(frazione));
}

/**
 * Lo sconto come lo usa il venditore in trattativa: quanto, e su quanto.
 * `quotaSuBase(5374.82, 21165.74)` → `"5.374,82 € (25,4%)"`.
 * Senza una base valida resta il solo importo: una percentuale su zero non
 * significa niente e non si inventa.
 */
export function quotaSuBase(importo, base, { decimali = 1 } = {}) {
  const testo = eur(importo);
  if (mancante(importo) || mancante(base) || Number(base) === 0) return testo;
  return `${testo} (${percentuale(Number(importo) / Number(base), { decimali })})`;
}
